/**
 * Orthogonal Connector Router
 * From code described here: https://medium.com/swlh/routing-orthogonal-diagram-connectors-in-javascript-191dc2c5ff70
 */

import { type IPoint } from 'fabric/fabric-impl';

type Direction = 'v' | 'h';
type Side = 'top' | 'right' | 'bottom' | 'left';

/**
 * A point in space
 */
interface Point {
  x: number;
  y: number;
}

/**
 * A size tuple
 */
interface Size {
  width: number;
  height: number;
}

/**
 * A line between two points
 */
interface Line {
  a: Point;
  b: Point;
}

/**
 * Represents a Rectangle by location and size
 */
interface Rect extends Size {
  left: number;
  top: number;
}

/**
 * Represents a connection point on a routing request
 */
export interface ConnectorPoint {
  point: Point;
  shape: Rect;
  side: Side;
}

/**
 * Byproduct data emitted by the routing algorithm
 */
export interface OrthogonalConnectorByproduct {
  hRulers: number[];
  vRulers: number[];
  spots: Point[];
  grid: Rectangle[];
  connections: Line[];
}

/**
 * Routing request data
 */
interface OrthogonalConnectorOpts {
  pointA: ConnectorPoint;
  pointB: ConnectorPoint;
  shapeMargin: number;
  globalBoundsMargin?: number;
  globalBounds: Rect;
}

function round(v) {
  return Math.round(v);
}

function roundPoint(p) {
  return { x: round(p.x), y: round(p.y) };
}

/**
 * Utility Point creator
 * @param x
 * @param y
 */
function makePt(x: number, y: number): Point {
  return { x, y };
}

/**
 * Computes distance between two points
 * @param a
 * @param b
 */
function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Abstracts a Rectangle and adds geometric utilities
 */
class Rectangle {
  static get empty(): Rectangle {
    return new Rectangle(0, 0, 0, 0);
  }

  static fromRect(r: Rect): Rectangle {
    return new Rectangle(r.left, r.top, r.width, r.height);
  }

  static fromLTRB(
    left: number,
    top: number,
    right: number,
    bottom: number
  ): Rectangle {
    return new Rectangle(left, top, right - left, bottom - top);
  }

  constructor(
    readonly left: number,
    readonly top: number,
    readonly width: number,
    readonly height: number
  ) {}

  contains(p: Point): boolean {
    return (
      p.x > this.left && p.x < this.right && p.y > this.top && p.y < this.bottom
    );
  }

  // We only consider the simple cases that we care about which are for
  // orthogonal lines, orthogonal rectangles, and points are always outside
  // the rectangle.
  intersectsLine(from: Point, to: Point): boolean {
    const sortedX = [from.x, to.x].sort((a, b) => a - b);
    const sortedY = [from.y, to.y].sort((a, b) => a - b);

    return (
      sortedX[0] < this.right &&
      sortedX[1] > this.left &&
      sortedY[0] < this.bottom &&
      sortedY[1] > this.top
    );
  }

  inflate(horizontal: number, vertical: number): Rectangle {
    return Rectangle.fromLTRB(
      this.left - horizontal,
      this.top - vertical,
      this.right + horizontal,
      this.bottom + vertical
    );
  }

  intersects(rectangle: Rectangle): boolean {
    const thisX = this.left;
    const thisY = this.top;
    const thisW = this.width;
    const thisH = this.height;
    const rectX = rectangle.left;
    const rectY = rectangle.top;
    const rectW = rectangle.width;
    const rectH = rectangle.height;
    return (
      rectX < thisX + thisW &&
      thisX < rectX + rectW &&
      rectY < thisY + thisH &&
      thisY < rectY + rectH
    );
  }

  union(r: Rectangle): Rectangle {
    const x = [this.left, this.right, r.left, r.right];
    const y = [this.top, this.bottom, r.top, r.bottom];
    return Rectangle.fromLTRB(
      Math.min(...x),
      Math.min(...y),
      Math.max(...x),
      Math.max(...y)
    );
  }

  get center(): Point {
    return {
      x: this.left + this.width / 2,
      y: this.top + this.height / 2,
    };
  }

  get right(): number {
    return this.left + this.width;
  }

  get bottom(): number {
    return this.top + this.height;
  }

  get location(): Point {
    return makePt(this.left, this.top);
  }

  get northEast(): Point {
    return { x: this.right, y: this.top };
  }

  get southEast(): Point {
    return { x: this.right, y: this.bottom };
  }

  get southWest(): Point {
    return { x: this.left, y: this.bottom };
  }

  get northWest(): Point {
    return { x: this.left, y: this.top };
  }

  get east(): Point {
    return makePt(this.right, this.center.y);
  }

  get north(): Point {
    return makePt(this.center.x, this.top);
  }

  get south(): Point {
    return makePt(this.center.x, this.bottom);
  }

  get west(): Point {
    return makePt(this.left, this.center.y);
  }

  get size(): Size {
    return { width: this.width, height: this.height };
  }
}

/**
 * Represents a node in a graph, whose data is a Point
 */
class PointNode {
  public distance = Number.MAX_SAFE_INTEGER;
  public shortestPaths: PointNode[][] = [];
  public adjacentNodes = new Map<PointNode, number>();
  constructor(public data: Point) {}
}

/***
 * Represents a Graph of Point nodes
 */
class PointGraph {
  private index: Record<string, Record<string, PointNode>> = {};

  midPoint: Point;

  add(p: Point) {
    const { x, y } = roundPoint(p);
    const xs = x.toString();
    const ys = y.toString();

    if (!(xs in this.index)) {
      this.index[xs] = {};
    }
    if (!(ys in this.index[xs])) {
      this.index[xs][ys] = new PointNode(p);
    }
  }

  private getLowestDistanceNode(unsettledNodes: Set<PointNode>): PointNode {
    let lowestDistanceNode: PointNode | null = null;
    let lowestDistance = Number.MAX_SAFE_INTEGER;
    for (const node of unsettledNodes) {
      const nodeDistance = node.distance;
      if (nodeDistance < lowestDistance) {
        lowestDistance = nodeDistance;
        lowestDistanceNode = node;
      }
    }
    return lowestDistanceNode!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
  }

  calculateShortestPathFromSource(
    graph: PointGraph,
    source: PointNode
  ): PointGraph {
    source.distance = 0;

    const settledNodes = new Set<PointNode>();
    const unsettledNodes = new Set<PointNode>();

    unsettledNodes.add(source);

    if (source.adjacentNodes.size === 0) {
      console.log('Unconnected start');
    }

    while (unsettledNodes.size !== 0) {
      const currentNode = this.getLowestDistanceNode(unsettledNodes);
      if (!currentNode) {
        console.log('Unexpectedly there is no lowest distance node');
        break;
      }

      unsettledNodes.delete(currentNode);

      for (const [adjacentNode, edgeWeight] of currentNode.adjacentNodes) {
        if (!settledNodes.has(adjacentNode)) {
          this.calculateMinimumDistance(adjacentNode, edgeWeight, currentNode);
          unsettledNodes.add(adjacentNode);
        }
      }
      settledNodes.add(currentNode);
    }

    return graph;
  }

  // Calculate the shortest distance, and shortest path to the evaluationNode.
  // Dijkstra's algorithm cannot handle turn costs or global optimizations so
  // we store *all* shortest paths, so that we can do a global optimation later.
  // This likely has very bad implications in the worst case, but in practice
  // our graphs are small enough that it doesn't matter. We limit paths to 100
  // as a safeguard against it blowing up.
  private calculateMinimumDistance(
    evaluationNode: PointNode,
    edgeWeight: number,
    sourceNode: PointNode
  ) {
    // Zero length edges cause loops (since there is zero cost to making the
    // transition) so add a small value.
    if (edgeWeight === 0) {
      edgeWeight = 0.001;
    }

    const newDistance = sourceNode.distance + edgeWeight;
    if (newDistance <= evaluationNode.distance) {
      if (newDistance < evaluationNode.distance) {
        // We found a shorter path, so clear the old ones.
        evaluationNode.shortestPaths = [];
        evaluationNode.distance = newDistance;
      }

      if (sourceNode.shortestPaths.length === 0) {
        evaluationNode.shortestPaths.push([sourceNode]);
      } else {
        for (
          let i = 0;
          i < Math.min(sourceNode.shortestPaths.length, 100);
          i++
        ) {
          // This is quite expensive given how frequently we do it. In profiling
          // it shows as the hotspot. However I don't see how to do it more
          // efficiently since we need to copy the array from the source node
          // (since the same source node may be used for multiple paths).
          const path = sourceNode.shortestPaths[i];
          const shortestPath: PointNode[] = [...path];
          shortestPath.push(sourceNode);
          evaluationNode.shortestPaths.push(shortestPath);
        }
      }
    }
  }

  private directionOf(a: Point, b: Point): Direction | null {
    if (a.x === b.x) {
      return 'h';
    } else if (a.y === b.y) {
      return 'v';
    } else {
      return null;
    }
  }

  private directionOfNodes(a: PointNode, b: PointNode): Direction | null {
    return this.directionOf(a.data, b.data);
  }

  connect(a: Point, b: Point) {
    a = roundPoint(a);
    b = roundPoint(b);
    const nodeA = this.get(a);
    const nodeB = this.get(b);

    if (!nodeA || !nodeB) {
      // throw new Error(`A point was not found`);
      // Don't connect points that don't exist.
      return;
    }

    nodeA.adjacentNodes.set(nodeB, distance(a, b));
  }

  has(p: Point): boolean {
    const { x, y } = p;
    const xs = x.toString();
    const ys = y.toString();
    return xs in this.index && ys in this.index[xs];
  }

  get(p: Point): PointNode | null {
    const { x, y } = p;
    const xs = x.toString();
    const ys = y.toString();

    if (xs in this.index && ys in this.index[xs]) {
      return this.index[xs][ys];
    }

    return null;
  }
}

/**
 * Gets the actual point of the connector based on the distance parameter
 * @param p
 */
function computePt(p: ConnectorPoint): Point {
  return makePt(p.point.x, p.point.y);
}

/**
 * Extrudes the connector point by margin depending on it's side
 * @param cp
 * @param margin
 */
function extrudeCp(cp: ConnectorPoint, margin: number): Point {
  const { x, y } = computePt(cp);
  switch (cp.side) {
    case 'top':
      return makePt(x, cp.shape.top - margin);
    case 'right':
      return makePt(cp.shape.left + cp.shape.width + margin, y);
    case 'bottom':
      return makePt(x, cp.shape.top + cp.shape.height + margin);
    case 'left':
      return makePt(cp.shape.left - margin, y);
  }
}

/**
 * Returns flag indicating if the side belongs on a vertical axis
 * @param side
 */
function isVerticalSide(side: Side): boolean {
  return side === 'top' || side === 'bottom';
}

/**
 * Creates a graph where the rulers intersect, avoiding crossing the obstacles
 * @param verticals
 * @param horizontals
 * @param bounds
 */
function rulersToGraph(
  verticals: number[],
  horizontals: number[],
  obstacles: Rectangle[]
): { graph: PointGraph; connections: Line[]; spots: Point[] } {
  const spots: Point[] = [];
  const graph = new PointGraph();
  const connections: Line[] = [];

  const pointInObstacle = (p: Point) =>
    obstacles.filter(o => o.contains(p)).length > 0;
  const lineThroughObstacle = (from: Point, to: Point) =>
    obstacles.filter(o => o.intersectsLine(from, to)).length > 0;

  // Only unique entries.
  verticals = [...new Set(verticals)];
  horizontals = [...new Set(horizontals)];

  verticals.sort((a, b) => a - b);
  horizontals.sort((a, b) => a - b);

  // Add points for first row and first column
  let lastP: IPoint | null = null;
  for (let yIndex = 0; yIndex < horizontals.length; yIndex++) {
    const p = makePt(verticals[0], horizontals[yIndex]);

    graph.add(p);
    spots.push(p);
    if (lastP) {
      graph.connect(p, lastP);
      graph.connect(lastP, p);
      connections.push({ a: p, b: lastP });
    }
    lastP = p;
  }
  lastP = null;
  for (let xIndex = 0; xIndex < verticals.length; xIndex++) {
    const p = makePt(verticals[xIndex], horizontals[0]);

    graph.add(p);
    spots.push(p);
    if (lastP) {
      graph.connect(p, lastP);
      graph.connect(lastP, p);
      connections.push({ a: p, b: lastP });
    }
    lastP = p;
  }

  for (let yIndex = 1; yIndex < horizontals.length; yIndex++) {
    for (let xIndex = 1; xIndex < verticals.length; xIndex++) {
      const p = makePt(verticals[xIndex], horizontals[yIndex]);
      const lastH = makePt(verticals[xIndex - 1], horizontals[yIndex]);
      const lastV = makePt(verticals[xIndex], horizontals[yIndex - 1]);

      if (!pointInObstacle(p)) {
        graph.add(p);
        spots.push(p);

        for (const lastPoint of [lastH, lastV]) {
          if (
            !lineThroughObstacle(p, lastPoint) &&
            !pointInObstacle(lastPoint)
          ) {
            graph.connect(p, lastPoint);
            graph.connect(lastPoint, p);
            connections.push({ a: p, b: lastPoint });
          }
        }
      }
    }
  }

  return { graph, connections, spots };
}

/**
 * Solves the shortest path for the origin-destination path of the graph
 * @param graph
 * @param origin
 * @param destination
 */
function shortestPath(
  graph: PointGraph,
  origin: Point,
  destination: Point
): Point[] {
  origin = roundPoint(origin);
  destination = roundPoint(destination);
  const originNode = graph.get(origin);
  const destinationNode = graph.get(destination);

  if (!originNode) {
    throw new Error(`Origin node {${origin.x},${origin.y}} not found`);
  }

  if (!destinationNode) {
    throw new Error(`Origin node {${origin.x},${origin.y}} not found`);
  }

  graph.calculateShortestPathFromSource(graph, originNode);

  // Add the destination node to the path (so far only the parth *to* the destination is calculated).
  const shortestPaths = destinationNode.shortestPaths;
  shortestPaths.forEach(path => path.push(destinationNode));

  // Compute the number of turns the line makes. We want to minimize turns.
  const numTurns = (path: PointNode[]) => {
    let turns = 0;
    for (let i = 1; i < path.length - 1; i++) {
      const prev = path[i - 1].data;
      const cur = path[i].data;
      const next = path[i + 1].data;

      if (
        (prev.x === cur.x && cur.x === next.x) ||
        (prev.y === cur.y && cur.y === next.y)
      ) {
        // Straight.
      } else {
        turns++;
      }
    }
    return turns;
  };

  // Compute the amount of the line that is on the midpoint between the two
  // objects, then invert to make into a penalty.
  const midPointScore = (path: PointNode[]) => {
    let totalLength = 0;
    let xMidpointLength = 0;
    let yMidpointLength = 0;
    for (let i = 1; i < path.length - 3; i++) {
      const fromPoint = path[i].data;
      const toPoint = path[i + 1].data;
      totalLength += Math.hypot(
        fromPoint.x - toPoint.x,
        fromPoint.y - toPoint.y
      );

      if (
        Math.floor(fromPoint.x) === Math.floor(toPoint.x) &&
        Math.floor(toPoint.x) === Math.floor(graph.midPoint.x)
      ) {
        xMidpointLength += Math.abs(fromPoint.y - toPoint.y);
      }
      if (
        Math.floor(fromPoint.y) === Math.floor(toPoint.y) &&
        Math.floor(toPoint.y) === Math.floor(graph.midPoint.y)
      ) {
        yMidpointLength += Math.abs(fromPoint.x - toPoint.x);
      }
    }
    return 1 - Math.min(1, (xMidpointLength + yMidpointLength) / totalLength);
  };

  // Sort the paths based on quality. The score is used like a penalty.
  shortestPaths.sort((a, b) => {
    const aScore = numTurns(a) + midPointScore(a);
    const bScore = numTurns(b) + midPointScore(b);

    return aScore - bScore;
  });

  if (shortestPaths.length > 0) {
    return shortestPaths[0].map(n => n.data);
  } else {
    return [];
  }
}

/**
 * Simplifies the path by removing unnecessary points, based on orthogonal pathways
 * @param points
 */
function simplifyPath(points: Point[]): Point[] {
  if (points.length <= 2) {
    return points;
  }

  let last = points[0];
  const r: Point[] = [points[0]];

  for (let i = 1; i < points.length - 1; i++) {
    const cur = points[i];
    const next = points[i + 1];

    // Skip identical points.
    if (cur.x === last.x && cur.y === last.y) {
      last = cur;
      continue;
    }

    // Skip aligned points.
    if (
      (cur.x === last.x && cur.x === next.x) ||
      (cur.y === last.y && cur.y === next.y)
    ) {
      last = cur;
      continue;
    }

    last = cur;
    r.push(cur);
  }
  r.push(points[points.length - 1]);

  return r;
}

// Calculate the minimum orthogonal distance between two rectangles.
function distanceBetweenRectangles(r1, r2) {
  const c1 = r1.center;
  const c2 = r2.center;

  return Math.max(
    Math.abs(c1.x - c2.x) - (r1.width + r2.width) / 2,
    Math.abs(c1.y - c2.y) - (r1.height + r2.height) / 2
  );
}

// Shrink the margin if the rectangles are too close together.
function computeMargin(margin, r1, r2) {
  const m = Math.floor(Math.min(margin, distanceBetweenRectangles(r1, r2) / 2));
  if (m <= 0) {
    return margin;
  } else {
    return m;
  }
}

/**
 * Routing logic.
 */
class OrthogonalRouter {
  readonly byproduct: OrthogonalConnectorByproduct = {
    hRulers: [],
    vRulers: [],
    spots: [],
    grid: [],
    connections: [],
  };

  route(opts: OrthogonalConnectorOpts): Point[] {
    const { pointA, pointB } = opts;
    const verticals: number[] = [];
    const horizontals: number[] = [];
    const sideA = pointA.side;
    const sideAVertical = isVerticalSide(sideA);
    const sideB = pointB.side;
    const sideBVertical = isVerticalSide(sideB);
    const originA = computePt(pointA);
    const originB = computePt(pointB);
    const shapeA = Rectangle.fromRect(pointA.shape);
    const shapeB = Rectangle.fromRect(pointB.shape);
    const shapeMargin = computeMargin(opts.shapeMargin, shapeA, shapeB);
    const inflatedA = shapeA.inflate(shapeMargin, shapeMargin);
    const inflatedB = shapeB.inflate(shapeMargin, shapeMargin);

    // Add edges to rulers
    // TODO: Add other shapes here to route around them.
    for (const b of [inflatedA, inflatedB]) {
      verticals.push(b.left);
      verticals.push(b.right);
      horizontals.push(b.top);
      horizontals.push(b.bottom);
    }

    // Rulers at origins of shapes
    (sideAVertical ? verticals : horizontals).push(
      sideAVertical ? originA.x : originA.y
    );
    (sideBVertical ? verticals : horizontals).push(
      sideBVertical ? originB.x : originB.y
    );

    // Rulers at midpoint between shapes.
    const xLimits = [
      inflatedA.left,
      inflatedA.right,
      inflatedB.left,
      inflatedB.right,
    ].sort((a, b) => a - b);
    const yLimits = [
      inflatedA.top,
      inflatedA.bottom,
      inflatedB.top,
      inflatedB.bottom,
    ].sort((a, b) => a - b);

    // If the shapes are closer than the margin there there is no midpoint.
    // If the shapes are further apart than the margin, then only the midpoint matters.
    if (xLimits[2] > xLimits[1]) {
      verticals.push((xLimits[1] + xLimits[2]) / 2);
    }
    if (yLimits[2] > yLimits[1]) {
      horizontals.push((yLimits[1] + yLimits[2]) / 2);
    }

    const start = computePt(pointA);
    const end = computePt(pointB);

    // End of the antenna from the object.
    // TODO: Needs to adjust to side, based on rotation angle.
    const origin = extrudeCp(pointA, shapeMargin);
    const destination = extrudeCp(pointB, shapeMargin);

    // Sort rulers
    verticals.sort((a, b) => a - b);
    horizontals.sort((a, b) => a - b);

    // Create grid
    // TODO: pass additional rectangles in here to route around other
    // close shapes.
    const { graph, connections, spots } = rulersToGraph(
      verticals,
      horizontals,
      [inflatedA, inflatedB]
    );

    // Add connections for the start and end. We do this after converting rulers
    // because these connectors should not route around obstacles.
    graph.add(start);
    spots.push(start);
    graph.connect(start, origin);
    graph.connect(origin, start);
    connections.push({ a: start, b: origin });
    graph.add(end);
    spots.push(end);
    graph.connect(end, destination);
    graph.connect(destination, end);
    connections.push({ a: destination, b: end });

    this.byproduct.spots = spots;
    this.byproduct.vRulers = verticals;
    this.byproduct.hRulers = horizontals;
    this.byproduct.connections = connections;

    // Store the midpoint of the midpoints. No midpoint when overlapping.
    const overlap = (a1, a2, b1, b2) =>
      Math.max(a1, a2) >= Math.min(b1, b2) &&
      Math.max(b1, b2) >= Math.min(a1, a2);
    graph.midPoint = makePt(
      overlap(shapeA.left, shapeA.right, shapeB.left, shapeB.right)
        ? -1000000
        : (xLimits[1] + xLimits[2]) / 2,
      overlap(shapeA.top, shapeA.bottom, shapeB.top, shapeB.bottom)
        ? -1000000
        : (yLimits[1] + yLimits[2]) / 2
    );

    const path = shortestPath(graph, start, end);

    if (path.length > 0) {
      return simplifyPath(path);
    } else {
      return [];
    }
  }
}

export default OrthogonalRouter;
